import { CountrySelector } from '@/components/common/CountrySelector';
import { FormFieldWrapper } from '@/components/forms/FormFieldWrapper';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ContractPartnerSchemaType } from '@/hooks/useContractPartnerForm';
import { UseFormReturn } from 'react-hook-form';

interface FormFieldsProps {
  form: UseFormReturn<ContractPartnerSchemaType>;
  tForms: (key: string) => string;
  languageOptions: Array<{ value: string; label: string }>;
  roleOptions: Array<{ value: string; label: string }>;
  titleOptions: Array<{ value: string; label: string }>;
}

/**
 * Contract Partner Form Fields Component
 * Reusable form fields following DRY principle and component composition
 */
export function ContractPartnerFormFields({
  form,
  tForms,
  languageOptions,
  roleOptions,
  titleOptions,
}: FormFieldsProps) {
  const hasDifferentAddress = form.watch('hasDifferentAddress');

  return (
    <>
      {/* Row 1: Language & Role */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='language'
          label={tForms('contractPartner.fields.language')}
          required
          error={form.formState.errors.language?.message}
        >
          <Select
            value={form.watch('language')}
            onValueChange={value => form.setValue('language', value)}
          >
            <SelectTrigger className='w-full'>
              <SelectValue
                placeholder={tForms(
                  'contractPartner.placeholders.selectLanguage'
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {languageOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormFieldWrapper>

        <FormFieldWrapper
          name='role'
          label={tForms('contractPartner.fields.role')}
          required
          error={form.formState.errors.role?.message}
        >
          <Select
            value={form.watch('role')}
            onValueChange={value => form.setValue('role', value)}
          >
            <SelectTrigger className='w-full'>
              <SelectValue
                placeholder={tForms('contractPartner.placeholders.selectRole')}
              />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormFieldWrapper>
      </div>

      {/* Row 2: Representative Switch & Different Address Switch */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='isRepresentative'
          label={tForms('contractPartner.fields.isRepresentative')}
          error={form.formState.errors.isRepresentative?.message}
        >
          <div className='flex items-center space-x-3 pt-2'>
            <Switch
              id='isRepresentative'
              checked={form.watch('isRepresentative')}
              onCheckedChange={checked =>
                form.setValue('isRepresentative', checked)
              }
            />
            <span className='text-sm text-muted-foreground'>
              {form.watch('isRepresentative')
                ? tForms('contractPartner.badges.representative')
                : tForms('contractPartner.descriptions.isRepresentative')}
            </span>
          </div>
        </FormFieldWrapper>

        <FormFieldWrapper
          name='hasDifferentAddress'
          label={tForms('contractPartner.fields.hasDifferentAddress')}
          error={form.formState.errors.hasDifferentAddress?.message}
        >
          <div className='flex items-center space-x-3 pt-2'>
            <Switch
              id='hasDifferentAddress'
              checked={form.watch('hasDifferentAddress')}
              onCheckedChange={checked =>
                form.setValue('hasDifferentAddress', checked)
              }
            />
            <span className='text-sm text-muted-foreground'>
              {form.watch('hasDifferentAddress')
                ? tForms('contractPartner.badges.differentAddress')
                : tForms('contractPartner.descriptions.hasDifferentAddress')}
            </span>
          </div>
        </FormFieldWrapper>
      </div>

      {/* Row 3: Title & First Name */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='title'
          label={tForms('contractPartner.fields.title')}
          error={form.formState.errors.title?.message}
        >
          <Select
            value={form.watch('title')}
            onValueChange={value => form.setValue('title', value)}
          >
            <SelectTrigger className='w-full'>
              <SelectValue
                placeholder={tForms('contractPartner.placeholders.selectTitle')}
              />
            </SelectTrigger>
            <SelectContent>
              {titleOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormFieldWrapper>

        <FormFieldWrapper
          name='firstName'
          label={tForms('contractPartner.fields.firstName')}
          required
          error={form.formState.errors.firstName?.message}
        >
          <Input
            id='firstName'
            type='text'
            {...form.register('firstName')}
            placeholder={tForms('contractPartner.placeholders.enterFirstName')}
            className='w-full'
          />
        </FormFieldWrapper>
      </div>

      {/* Row 4: Last Name & Phone */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='lastName'
          label={tForms('contractPartner.fields.lastName')}
          required
          error={form.formState.errors.lastName?.message}
        >
          <Input
            id='lastName'
            type='text'
            {...form.register('lastName')}
            placeholder={tForms('contractPartner.placeholders.enterLastName')}
            className='w-full'
          />
        </FormFieldWrapper>

        <FormFieldWrapper
          name='phone'
          label={tForms('contractPartner.fields.phone')}
          error={form.formState.errors.phone?.message}
        >
          <Input
            id='phone'
            type='tel'
            {...form.register('phone')}
            placeholder={tForms('contractPartner.placeholders.enterPhone')}
            className='w-full'
          />
        </FormFieldWrapper>
      </div>

      {/* Row 5: Mobile & Email */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='mobile'
          label={tForms('contractPartner.fields.mobile')}
          error={form.formState.errors.mobile?.message}
        >
          <Input
            id='mobile'
            type='tel'
            {...form.register('mobile')}
            placeholder={tForms('contractPartner.placeholders.enterMobile')}
            className='w-full'
          />
        </FormFieldWrapper>

        <FormFieldWrapper
          name='email'
          label={tForms('contractPartner.fields.email')}
          required
          error={form.formState.errors.email?.message}
        >
          <Input
            id='email'
            type='email'
            {...form.register('email')}
            placeholder={tForms('contractPartner.placeholders.enterEmail')}
            className='w-full'
          />
        </FormFieldWrapper>
      </div>

      {/* Conditional Address Fields */}
      {hasDifferentAddress && (
        <div className='space-y-6 border-t pt-6'>
          <h3 className='text-lg font-semibold'>
            {tForms('contractPartner.dialog.addressSectionTitle')}
          </h3>

          {/* Street and House Number */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormFieldWrapper
              name='street'
              label={tForms('contractPartner.fields.street')}
              required={hasDifferentAddress}
              error={form.formState.errors.street?.message}
            >
              <Input
                id='street'
                type='text'
                {...form.register('street')}
                placeholder={tForms('contractPartner.placeholders.enterStreet')}
                className='w-full'
              />
            </FormFieldWrapper>

            <FormFieldWrapper
              name='houseNumber'
              label={tForms('contractPartner.fields.houseNumber')}
              required={hasDifferentAddress}
              error={form.formState.errors.houseNumber?.message}
            >
              <Input
                id='houseNumber'
                type='text'
                {...form.register('houseNumber')}
                placeholder={tForms('contractPartner.placeholders.houseNumber')}
                className='w-full'
              />
            </FormFieldWrapper>
          </div>

          {/* Postal Code and City */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormFieldWrapper
              name='postalCode'
              label={tForms('contractPartner.fields.postalCode')}
              required={hasDifferentAddress}
              error={form.formState.errors.postalCode?.message}
            >
              <Input
                id='postalCode'
                type='text'
                {...form.register('postalCode')}
                placeholder={tForms(
                  'contractPartner.placeholders.enterPostalCode'
                )}
                pattern='[0-9]*'
                inputMode='numeric'
                className='w-full'
              />
            </FormFieldWrapper>

            <FormFieldWrapper
              name='city'
              label={tForms('contractPartner.fields.city')}
              required={hasDifferentAddress}
              error={form.formState.errors.city?.message}
            >
              <Input
                id='city'
                type='text'
                {...form.register('city')}
                placeholder={tForms('contractPartner.placeholders.enterCity')}
                className='w-full'
              />
            </FormFieldWrapper>
          </div>

          {/* Country and State */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormFieldWrapper
              name='country'
              label={tForms('contractPartner.fields.country')}
              required={hasDifferentAddress}
              error={form.formState.errors.country?.message}
            >
              <CountrySelector
                value={form.watch('country')}
                onValueChange={value => form.setValue('country', value)}
                placeholder={tForms(
                  'contractPartner.placeholders.selectCountry'
                )}
                error={form.formState.errors.country?.message}
                required={hasDifferentAddress}
                name='country'
              />
            </FormFieldWrapper>

            <FormFieldWrapper
              name='state'
              label={tForms('contractPartner.fields.state')}
              error={form.formState.errors.state?.message}
            >
              <Input
                id='state'
                type='text'
                {...form.register('state')}
                placeholder={tForms('contractPartner.placeholders.enterState')}
                className='w-full'
              />
            </FormFieldWrapper>
          </div>
        </div>
      )}
    </>
  );
}
